import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreditCardIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  CalendarIcon,
  ArrowRightIcon,
  ArrowDownTrayIcon,
  DeviceTabletIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';
import { useToastStore } from '../../store/toastStore';
import { StatCard } from '../../components/billing/StatCard';
import { UsageBar } from '../../components/billing/UsageBar';
import { StatusBadge } from '../../components/billing/StatusBadge';
import type { DownloadHistoryItem } from '../../types/device';
import { BackupPasswordBanner } from '../../components/ui/BackupPasswordBanner';

function formatTokens(tokens: number): string {
  if (tokens >= 1000000) return `${(tokens / 1000000).toFixed(1)}M`;
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(0)}K`;
  return tokens.toString();
}

export function DashboardPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.get('verified') === 'true') {
      useToastStore.getState().addToast({
        type: 'success',
        title: 'Email verified',
        message: 'Your email address has been verified successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      searchParams.delete('verified');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  const { data: usage } = useQuery({
    queryKey: ['usage'],
    queryFn: () => api.getMyUsage(),
  });

  const { data: recentDownloads } = useQuery({
    queryKey: ['downloads', 'recent'],
    queryFn: () => api.getMyDownloads({ page: 1, limit: 5 }),
  });

  const tokensUsed = usage?.tokens_used ?? 0;
  const tokensTotal = (usage?.tokens_included ?? 0) + (usage?.tokens_rolled_over ?? 0);
  const daysRemaining = usage?.days_remaining ?? 0;
  const planName = usage?.package?.name || 'No Plan';
  const planStatus = usage?.subscription?.status || null;
  const deviceCount = usage?.device_count ?? 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Only shown to accounts that sign in by emailed link and have not
          already declined — see BackupPasswordBanner. */}
      <BackupPasswordBanner />

      {/* Welcome Banner */}
      <div className="card bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700/80 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="relative z-10">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-slate-400">
            Here's an overview of your VortexNav activity and billing status.
          </p>
        </div>
      </div>

      {/* Stat Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <StatCard
          title="Current Plan"
          icon={CreditCardIcon}
          value={planName}
          subtext={
            <div className="mt-2 flex h-6 items-center">
              {planStatus ? (
                <StatusBadge status={planStatus as any} />
              ) : (
                'Upgrade to get tokens'
              )}
            </div>
          }
        />

        <StatCard
          title="Token Usage"
          icon={GlobeAltIcon}
          value={`${formatTokens(tokensUsed)} / ${formatTokens(tokensTotal)}`}
          subtext={
            <div className="mt-2 flex h-6 items-center gap-2">
              {tokensTotal > 0 ? (
                <>
                  <div className="h-1.5 flex-1 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-cyan-500 rounded-full"
                      style={{
                        width: `${Math.min((tokensUsed / tokensTotal) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-slate-400">
                    {((tokensUsed / tokensTotal) * 100).toFixed(0)}%
                  </span>
                </>
              ) : (
                'No tokens allocated'
              )}
            </div>
          }
        />

        <Link to="/dashboard/downloads" className="block h-full hover:ring-1 hover:ring-cyan-500/30 rounded-xl transition-all">
          <StatCard
            className="h-full"
            title="Tiles Downloaded"
            icon={ArrowDownTrayIcon}
            value={tokensUsed.toLocaleString()}
            subtext={
              <div className="mt-2 flex h-6 items-center">
                This billing period
              </div>
            }
          />
        </Link>

        <StatCard
          title="Active Devices"
          icon={DevicePhoneMobileIcon}
          value={deviceCount.toString()}
          subtext={
            <div className="mt-2 flex h-6 items-center">Linked to account</div>
          }
        />
      </div>

      {/* Large Usage Gauge Section */}
      {usage?.subscription && (
        <div className="card">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                Quarterly Token Usage
              </h2>
              <p className="text-sm text-slate-400 mt-1 flex items-center gap-1.5">
                <CalendarIcon className="w-4 h-4" />
                {daysRemaining} days remaining in billing period
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/dashboard/billing"
                className="btn-secondary text-sm flex items-center gap-2"
              >
                Manage Plan
              </Link>
              <Link
                to="/dashboard/billing#invoices"
                className="btn-primary text-sm flex items-center gap-2"
              >
                View Invoices <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50">
            <UsageBar
              used={tokensUsed}
              total={tokensTotal}
              label="Tokens Used"
              className="mb-2"
            />
            <div className="mt-4 flex items-start gap-2 text-sm text-amber-400/80 bg-amber-500/5 p-3 rounded-lg border border-amber-500/10">
              <span className="font-medium">Note:</span>
              Tokens are allocated per billing period. When your balance reaches
              zero, tile downloads will pause. You can upgrade your plan at any
              time for additional capacity.
            </div>
          </div>
        </div>
      )}

      {/* Recent Downloads */}
      {recentDownloads?.data && recentDownloads.data.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">Recent Downloads</h2>
            <Link
              to="/dashboard/downloads"
              className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
            >
              View All <ArrowRightIcon className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="space-y-3">
            {recentDownloads.data.map((dl: DownloadHistoryItem) => (
              <div
                key={dl.id}
                className="flex items-center justify-between py-2 border-b border-slate-700/50 last:border-0"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-1.5 rounded-lg bg-slate-800 text-slate-400">
                    <DeviceTabletIcon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {dl.region.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {dl.device.name || dl.device.type} &middot;{' '}
                      {new Date(dl.requested_at).toLocaleDateString('en-NZ', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <p className="text-sm text-slate-300">
                    {dl.tiles_downloaded.toLocaleString()} tiles
                  </p>
                  <p className="text-xs text-slate-500 capitalize">{dl.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No subscription CTA */}
      {!usage?.subscription && (
        <div className="card text-center py-12">
          <GlobeAltIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No active subscription</h3>
          <p className="text-slate-400 mb-6">
            Subscribe to get monthly token allowance for tile downloads.
          </p>
          <Link to="/pricing" className="btn-primary">
            Choose a Plan
          </Link>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
